import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, Chip, Stack,
  CircularProgress, List, ListItem, ListItemIcon, ListItemText, Alert,
  AlertTitle, Link, MenuItem, Select, FormControl, InputLabel, Paper,
  Divider, Tooltip, TextField,
} from '@mui/material';
import ReactFlow, { MiniMap, Controls, Background, useNodesState, useEdgesState, MarkerType, Position } from 'reactflow';
import 'reactflow/dist/style.css';
import { Add, Download, CheckCircle, TipsAndUpdates, AssignmentTurnedIn, FilterList, TrendingUp, EmojiEvents, School, Assessment } from '@mui/icons-material';
import { LineChart } from '@mui/x-charts';
import CommonTable from '../../components/Shared/Tables/TableV1.tsx';
import SearchableDropdown from '../../components/FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import CreateTest from './CreateTest.tsx';
import { AiActions } from '../../Services/_actions/Ai/ai.actions.ts';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../Services/_states/auth.js';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TopicMasteryItem {
  Topic: string; Subtopic: string; Progress: string; status: string;
  tests_taken?: number; average_score?: number; best_score?: number;
  worst_score?: number; trend?: string; last_tested?: string;
}

interface LearningMaterial { title: string; url: string; }

interface AIRecommendations {
  recommended_learning_path?: string;
  improvement_tips?: string[];
  focus_areas?: string[];
  learning_materials?: LearningMaterial[];
  suggested_followup_test?: string;
  estimated_improvement_time?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  Mastered: { bg: '#f0fdf4', color: '#10b981', border: '#10b981' },
  Good: { bg: '#fffbeb', color: '#f59e0b', border: '#f59e0b' },
  Moderate: { bg: '#fffbeb', color: '#f59e0b', border: '#f59e0b' },
  'Needs Improvement': { bg: '#fef2f2', color: '#ef4444', border: '#ef4444' },
};

const DEFAULT_STATUS_COLOR = { bg: '#fef2f2', color: '#ef4444', border: '#ef4444' };

const getStatusStyle = (status: string) =>
  STATUS_COLORS[status] ?? DEFAULT_STATUS_COLOR;

const getProgressColor = (progress: string) => {
  const v = parseInt(progress) || 0;
  if (v >= 80) return { bg: '#f0fdf4', border: '#10b981', text: '#166534', edge: '#10b981' };
  if (v >= 60) return { bg: '#eff6ff', border: '#3b82f6', text: '#1e40af', edge: '#3b82f6' };
  if (v >= 40) return { bg: '#fffbeb', border: '#f59e0b', text: '#78350f', edge: '#f59e0b' };
  return { bg: '#fef2f2', border: '#ef4444', text: '#991b1b', edge: '#ef4444' };
};

const parseTopicsToList = (str: string): string[] =>
  str ? str.split(',').map(s => s.trim()).filter(Boolean) : [];

const normalizeTestedTopics = (testedTopics: any): Record<string, string[]> => {
  if (!testedTopics) return {};
  if (!Array.isArray(testedTopics)) return testedTopics;
  return testedTopics.reduce((acc: Record<string, string[]>, curr: any) => {
    Object.entries(curr || {}).forEach(([k, v]) => {
      acc[k] = [...(acc[k] || []), ...(v as string[])];
    });
    return acc;
  }, {});
};

const getFromStorage = <T,>(key: string, getter: (parsed: any) => T): T | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? getter(JSON.parse(raw)) : null;
  } catch { return null; }
};

// ─── Table Column Definitions ─────────────────────────────────────────────────

const StatusChip = ({ value }: { value: string }) => {
  const style = getStatusStyle(value);
  return (
    <Chip label={value} size="small" sx={{
      bgcolor: style.bg, color: style.color, fontWeight: 600, fontSize: 11,
      height: 24, border: '1px solid', borderColor: `${style.border}40`,
    }} />
  );
};

const topicColumns = [
  {
    accessorKey: 'topic', header: 'Topic', enableSorting: false,
    Cell: ({ cell }: any) => <Typography fontWeight={600} fontSize={13} sx={{ color: '#1e293b' }}>{cell.getValue()}</Typography>
  },
  {
    accessorKey: 'subtopic', header: 'Sub Topic', enableSorting: false,
    Cell: ({ cell }: any) => <Typography fontWeight={500} fontSize={12} sx={{ color: '#64748b' }}>{cell.getValue() || '-'}</Typography>
  },
  {
    accessorKey: 'tests_taken', header: 'Tests',
    Cell: ({ cell }: any) => <Chip label={cell.getValue()} size="small" sx={{ bgcolor: '#eff6ff', color: 'primary.main', fontSize: 11, fontWeight: 600, height: 24, border: '1px solid', borderColor: 'primary.light' }} />
  },
  {
    accessorKey: 'average_score', header: 'Avg Score',
    Cell: ({ cell }: any) => {
      const v = cell.getValue();
      const display = typeof v === 'number' ? (Number.isInteger(v) ? v : v.toFixed(2)) : v;
      return <Typography fontWeight={600} fontSize={12} sx={{ color: '#475569' }}>{display}%</Typography>;
    }
  },
  {
    accessorKey: 'best_score', header: 'Best',
    Cell: ({ cell }: any) => <Typography fontWeight={700} fontSize={12} sx={{ color: '#10b981' }}>{cell.getValue()}%</Typography>
  },
  {
    accessorKey: 'status', header: 'Status', enableSorting: false,
    Cell: ({ cell }: any) => <StatusChip value={cell.getValue()} />
  },
];

const testHistoryColumns = [
  {
    accessorKey: 'test_id', header: 'Test ID', enableSorting: false,
    Cell: ({ cell }: any) => <Chip label={cell.getValue()} size="small" sx={{ bgcolor: '#f8fafc', color: '#475569', fontSize: 11, fontWeight: 600, fontFamily: 'monospace', border: '1px solid #e2e8f0' }} />
  },
  {
    accessorKey: 'topic', header: 'Topic', enableSorting: false,
    Cell: ({ cell }: any) => <Typography fontSize={12} fontWeight={500} sx={{ color: '#334155' }}>{cell.getValue()}</Typography>
  },
  {
    accessorKey: 'subtopic', header: 'Sub Topic', enableSorting: false,
    Cell: ({ cell }: any) => <Typography fontSize={11} sx={{ color: '#64748b' }}>{cell.getValue() || '-'}</Typography>
  },
  {
    accessorKey: 'date', header: 'Date',
    Cell: ({ cell }: any) => (
      <Typography fontSize={11} color="text.secondary" fontWeight={500}>
        {new Date(cell.getValue()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </Typography>
    )
  },
  {
    accessorKey: 'score_display', header: 'Score',
    Cell: ({ cell }: any) => (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: '#eff6ff', px: 1.5, py: 0.5, borderRadius: '6px', border: '1px solid', borderColor: 'primary.light' }}>
        <Typography fontWeight={700} fontSize={13} sx={{ color: 'primary.main' }}>{cell.getValue()}</Typography>
      </Box>
    )
  },
  {
    accessorKey: 'performance', header: 'Performance', enableSorting: false,
    Cell: ({ cell }: any) => <StatusChip value={cell.getValue()} />
  },
  {
    accessorKey: 'view_result', header: 'Action', enableSorting: false,
    Cell: ({ row }: any) => (
      <Button variant="contained" size="small"
        onClick={() => window.open(`${window.location.origin}/r/test/${row.original.test_id}`, '_blank')}
        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '11px', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}>
        View Result
      </Button>
    )
  },
];

// ─── KnowledgeMapFlow ─────────────────────────────────────────────────────────

const KnowledgeMapFlow = ({ data, onSubtopicClick }: any) => {
  const { initialNodes, initialEdges } = useMemo(() => {
    const grouped = data.reduce((acc: any, item: any) => {
      (acc[item.topic] = acc[item.topic] || []).push(item);
      return acc;
    }, {});

    const nodes: any[] = [], edges: any[] = [];
    let nodeId = 0, yOffset = 0;

    Object.entries(grouped).forEach(([topic, subtopics]: [string, any]) => {
      const subjectNodeId = `subject-${nodeId}`;
      nodes.push({
        id: subjectNodeId, type: 'default',
        position: { x: 50, y: yOffset },
        sourcePosition: Position.Right, targetPosition: Position.Left,
        data: {
          label: (
            <Box sx={{ p: 2, minWidth: 200, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', fontSize: 14 }}>{topic}</Typography>
            </Box>
          ),
        },
        style: { background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)', border: 'none', width: 220, borderRadius: 8, padding: 0, boxShadow: '0 4px 12px rgba(59,130,246,0.3)' },
      });
      nodeId++;

      subtopics.forEach((item: any, subIndex: number) => {
        const subtopicNodeId = `subtopic-${nodeId}`;
        const colors = getProgressColor(item.progress);
        nodes.push({
          id: subtopicNodeId, type: 'default',
          position: { x: 350, y: yOffset + subIndex * 140 },
          sourcePosition: Position.Right, targetPosition: Position.Left,
          data: {
            label: (
              <Box onClick={() => onSubtopicClick(item)} sx={{ p: 1.5, cursor: 'pointer', width: 180, '&:hover': { transform: 'scale(1.03)', transition: 'all 0.2s' } }}>
                <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 12, mb: 1, lineHeight: 1.3, wordBreak: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {item.subtopic || item.topic}
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: 10, fontWeight: 500, mb: 1 }}>{item.progress}</Typography>
                <Chip label={item.status} size="small" sx={{ bgcolor: colors.bg, color: colors.text, fontSize: 9, fontWeight: 600, width: '100%', height: 20, border: '1px solid', borderColor: colors.border, '& .MuiChip-label': { padding: '0 6px' } }} />
              </Box>
            ),
          },
          style: { background: '#ffffff', border: `2px solid ${colors.border}`, borderRadius: 8, padding: 0, width: 220, minHeight: 110, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
        });
        edges.push({
          id: `e-${subjectNodeId}-${subtopicNodeId}`, source: subjectNodeId, target: subtopicNodeId,
          type: 'smoothstep', style: { stroke: colors.edge, strokeWidth: 3 },
          markerEnd: { type: MarkerType.ArrowClosed, color: colors.edge },
        });
        nodeId++;
      });

      yOffset += Math.max(subtopics.length * 140, 200) + 50;
    });

    return { initialNodes: nodes, initialEdges: edges };
  }, [data]);

  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  return (
    <Box sx={{ height: 450, width: '100%', bgcolor: '#f8fafc', borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
      <ReactFlow nodes={nodes} edges={edges} nodesDraggable onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} fitView attributionPosition="bottom-left">
        <Background color="#e2e8f0" gap={16} />
        <Controls />
        <MiniMap nodeColor={(n) => n.id.startsWith('subject') ? '#3b82f6' : '#94a3b8'} maskColor="rgba(0,0,0,0.1)" />
      </ReactFlow>
    </Box>
  );
};

// ─── LearningMaterialsDialog ──────────────────────────────────────────────────

const LearningMaterialsDialog = ({ open, onClose, subtopic }: any) => {
  if (!open || !subtopic) return null;
  const color = getStatusStyle(subtopic.status).color;

  return (
    <Box onClick={onClose} sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, p: 2 }}>
      <Card onClick={(e) => e.stopPropagation()} sx={{ maxWidth: 600, width: '100%', maxHeight: '85vh', overflow: 'auto', borderRadius: 2, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 1, fontSize: 16 }}>{subtopic.subtopic || subtopic.topic}</Typography>
              <Chip label={subtopic.topic} size="small" sx={{ bgcolor: '#eff6ff', color: 'primary.main', fontWeight: 600, fontSize: 11, border: '1px solid', borderColor: 'primary.light' }} />
            </Box>
            <Button onClick={onClose} size="small" sx={{ minWidth: 'auto', width: 36, height: 36, borderRadius: '8px', bgcolor: '#f1f5f9', color: '#475569', '&:hover': { bgcolor: '#e2e8f0' } }}>✕</Button>
          </Box>

          <Paper sx={{ bgcolor: '#f8fafc', p: 3, borderRadius: 2, mb: 3, border: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>Current Progress</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color, mt: 0.5, fontSize: 28 }}>{subtopic.progress}</Typography>
              </Box>
              <Chip label={subtopic.status} sx={{ bgcolor: `${color}15`, color, fontWeight: 600, fontSize: 12, height: 30, px: 1.5, border: '1px solid', borderColor: color }} />
            </Box>
          </Paper>

          {subtopic.prerequisites?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#1e293b', fontSize: 13 }}>Prerequisites</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {subtopic.prerequisites.map((p: string, i: number) => (
                  <Chip key={i} label={p} size="small" sx={{ bgcolor: '#fffbeb', color: '#f59e0b', fontWeight: 600, fontSize: 11, border: '1px solid #fcd34d', '&:hover': { bgcolor: '#fef3c7' } }} />
                ))}
              </Stack>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#1e293b', fontSize: 13 }}>Learning Materials</Typography>
            {subtopic.learning_materials?.length > 0 ? (
              <Stack spacing={1.5}>
                {subtopic.learning_materials.map((m: any, i: number) => (
                  <Link key={i} href={m.url} target="_blank" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', textDecoration: 'none', color: 'primary.main', fontWeight: 600, fontSize: 13, transition: 'all 0.2s', '&:hover': { bgcolor: '#f8fafc', borderColor: 'primary.main' } }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: '6px', bgcolor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main', fontWeight: 700 }}>→</Box>
                    <span style={{ flex: 1 }}>{m.title}</span>
                  </Link>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary" fontSize={13}>No learning materials available</Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

// ─── ResourceLink ─────────────────────────────────────────────────────────────

const ResourceLink = ({ href, title }: { href: string; title: string }) => (
  <Link href={href} target="_blank" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e2e8f0', textDecoration: 'none', color: 'primary.dark', fontWeight: 600, fontSize: 13, transition: 'all 0.2s', '&:hover': { borderColor: 'primary.main', bgcolor: '#f8fafc', color: 'primary.main' } }}>
    <Box sx={{ width: 32, height: 32, borderRadius: '6px', bgcolor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main', fontWeight: 700 }}>→</Box>
    <span style={{ flex: 1 }}>{title}</span>
  </Link>
);

// ─── StatCard ─────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, icon, tooltip }: any) => (
  <Tooltip title={tooltip} arrow>
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, bgcolor: 'white', border: '1px solid #e2e8f0', height: 140, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', transition: 'all 0.2s', cursor: 'pointer', '&:hover': { borderColor: 'primary.main', boxShadow: '0 4px 12px rgba(59,130,246,0.15)' } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b' }}>{label}</Typography>
        <Box sx={{ color: 'primary.main' }}>{icon}</Box>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 600, fontSize: value?.length > 15 ? '1.1rem' : value?.length > 8 ? '1.35rem' : '1.75rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Paper>
  </Tooltip>
);

// ─── TopicList ────────────────────────────────────────────────────────────────

const TopicList = ({ title, items, color, bg, border, icon }: any) => {
  if (!items.length) return null;
  return (
    <Grid item xs={12} md={6}>
      <Paper elevation={0} sx={{ overflow: 'hidden', height: '100%', bgcolor: 'transparent' }}>
        <CardContent sx={{ p: 0 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color, display: 'flex', alignItems: 'center', gap: 1, fontSize: 15 }}>
            {icon} {title}
          </Typography>
          <Box sx={{ p: 2.5, height: { md: '200px', xs: 'auto' }, overflowY: 'auto', bgcolor: bg, borderRadius: 2, border: `1px solid ${border}` }}>
            <List dense sx={{ py: 0 }}>
              {items.map((item: string, i: number) => (
                <ListItem key={i} sx={{ py: 0.5, px: 0, alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 28, mt: 0.3 }}>{React.cloneElement(icon, { sx: { fontSize: 14, color } })}</ListItemIcon>
                  <ListItemText primary={item} primaryTypographyProps={{ fontSize: 12, fontWeight: 500, lineHeight: 1.5, color: color === '#10b981' ? '#166534' : '#991b1b' }} />
                </ListItem>
              ))}
            </List>
          </Box>
        </CardContent>
      </Paper>
    </Grid>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ParentDashboard: React.FC = () => {
  const [showCreateTest, setShowCreateTest] = useState(false);
  const [performanceData, setPerformanceData] = useState<any>(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState('All');
  const [selectedSubtopic, setSelectedSubtopic] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [studentOptions, setStudentOptions] = useState<any[]>([]);
  const [sorting, setSorting] = useState([]);
  const [tmTopicFilter, setTmTopicFilter] = useState('');
  const [tmSubtopicFilter, setTmSubtopicFilter] = useState('');
  const [thTopicFilter, setThTopicFilter] = useState('');
  const [thSubtopicFilter, setThSubtopicFilter] = useState('');

  const actions = AiActions();
  const loggedInUser = useRecoilValue(userInformationAtom);

  // Reset subtopic filter when topic changes
  useEffect(() => { setTmSubtopicFilter(''); }, [tmTopicFilter]);
  useEffect(() => { setThSubtopicFilter(''); }, [thTopicFilter]);

  // Match logged-in user to student options
  useEffect(() => {
    if (!loggedInUser || !studentOptions.length) return;
    const match = studentOptions.find(o =>
      o.studentData?.mobile === loggedInUser.mobile ||
      o.studentData?.username === loggedInUser.username
    );
    if (match) setSelectedStudent(match);
  }, [loggedInUser, studentOptions]);

  const fetchStudents = useCallback(async (mobile?: string) => {
    if (!mobile) return;
    try {
      const data = await actions.getRecordEntity(mobile, { template_form_type: 'contact_form' });
      if (data?.items?.length) {
        const options = data.items.map((item: any) => ({
          label: item.entity?.username || 'Unknown Student',
          value: item.entity?.entity_id || item._id,
          studentData: item.entity,
        }));
        setStudentOptions(options);
        if (!selectedStudent) setSelectedStudent(options[0]);
      }
    } catch (e) { console.error('Failed to get entity record', e); }
  }, []);

  useEffect(() => { fetchStudents(loggedInUser?.mobile); }, [loggedInUser?.mobile]);

  const fetchStudentPerformance = useCallback(async () => {
    const userId = getFromStorage('userInfo', p => p?.id);
    const rawMobile = getFromStorage('userInfo', p => p?.mobile);
    if (!userId || !rawMobile) return;
    const phoneNumber = rawMobile.toString().length === 10 ? `91${rawMobile}` : rawMobile.toString();

    setLoadingPerformance(true);
    try {
      const response = await actions.getStudentPerformance(userId, phoneNumber);
      setPerformanceData(response);
    } catch (e) { console.error('Error fetching student performance:', e); }
    finally { setLoadingPerformance(false); }
  }, []);

  useEffect(() => { fetchStudentPerformance(); }, []);

  // ─── Derived Data ───────────────────────────────────────────────────────────

  const availableSubjects = useMemo(() =>
    performanceData?.data?.Subjects ? ['All', ...Object.keys(performanceData.data.Subjects)] : ['All'],
    [performanceData]);

  const currentSubjectData = useMemo(() => {
    if (!performanceData?.data?.Subjects) return null;
    const allSubjects = Object.values(performanceData.data.Subjects) as any[];

    if (selectedSubject === 'All') {
      return {
        overall_average_score_display: performanceData.data.overall_average_score_display,
        'Tests Completed': performanceData.data['Tests Completed'],
        overall_average_performance: performanceData.data.overall_average_performance,
        'Topic Mastery': allSubjects.flatMap(s => s['Topic Mastery'] || []),
        'Test History': allSubjects.flatMap(s => s['Test History'] || []),
        'Performance Trend': performanceData.data['Performance Trend'] || [],
        'performance-map': allSubjects.flatMap(s => s['performance-map'] || []),
        tested_topics: normalizeTestedTopics(allSubjects.flatMap(s => s.tested_topics || [])),
      };
    }

    const subject = performanceData.data.Subjects[selectedSubject];
    if (!subject) return null;
    const aiRec = subject['AI-Generated Learning Recommendations'] || {};

    return {
      overall_average_score_display: subject['Overall Performance'],
      'Tests Completed': subject['Tests Completed'] || 0,
      overall_average_performance: subject.average_performance,
      'Topic Mastery': subject['Topic Mastery'] || [],
      'Test History': subject['Test History'] || [],
      'Performance Trend': performanceData.data['Performance Trend'] || [],
      'performance-map': allSubjects.flatMap(s => s['performance-map'] || []),
      tested_topics: normalizeTestedTopics(subject.tested_topics),
      'AI-Generated Learning Recommendations': {
        recommended_learning_path: aiRec.recommended_learning_path || 'N/A',
        improvement_tips: aiRec.improvement_tips || aiRec.focus_areas || [],
        focus_areas: aiRec.focus_areas || [],
        learning_materials: aiRec.learning_materials || [],
        suggested_followup_test: aiRec.suggested_followup_test || '',
        estimated_improvement_time: aiRec.estimated_improvement_time || '',
      },
    };
  }, [performanceData, selectedSubject]);

  const topicMasteryData = useMemo(() =>
    currentSubjectData?.['Topic Mastery']?.map((item: any) => ({
      topic: item.Topic || 'Unknown Topic', subtopic: item.Subtopic || '-',
      progress: item.Progress || '0%', status: item.status || 'Unknown',
      tests_taken: item.tests_taken || 0, average_score: item.average_score || 0,
      best_score: item.best_score || 0, worst_score: item.worst_score || 0,
      trend: item.trend || 'N/A', last_tested: item.last_tested || 'N/A',
    })) || [], [currentSubjectData]);

  const testHistoryData = useMemo(() =>
    currentSubjectData?.['Test History']?.map((item: any) => ({
      test_id: item.test_id || 'N/A', test: item.test || 'N/A',
      subject: item.subject || 'N/A', topic: item.topic || 'N/A',
      subtopic: item.subtopic || '-', date: item.date || new Date().toISOString(),
      score: item.score || 0, score_display: item.score_display || '0/0',
      performance: item.performance || 'N/A',
    })) || [], [currentSubjectData]);

  const testedTopics = currentSubjectData?.tested_topics || {};
  const topics = Object.keys(testedTopics).filter(Boolean);
  const subtopics = tmTopicFilter ? testedTopics[tmTopicFilter] || [] : [];
  const thSubtopics = thTopicFilter ? testedTopics[thTopicFilter] || [] : [];

  const filteredTopicMasteryData = useMemo(() =>
    topicMasteryData.filter(item =>
      (!tmTopicFilter || item.topic === tmTopicFilter) &&
      (!tmSubtopicFilter || item.subtopic === tmSubtopicFilter)
    ), [topicMasteryData, tmTopicFilter, tmSubtopicFilter]);

  const filteredTestHistoryData = useMemo(() =>
    testHistoryData.filter(item =>
      (!thTopicFilter || item.topic === thTopicFilter) &&
      (!thSubtopicFilter || item.subtopic === thSubtopicFilter)
    ), [testHistoryData, thTopicFilter, thSubtopicFilter]);

  const performanceMapData = useMemo(() =>
    currentSubjectData?.['performance-map']?.map((item: any) => {
      const mastery = topicMasteryData.find(t => t.topic === item.topic || t.subtopic === item.subtopic);
      return { topic: item.topic || 'Unknown', subtopic: item.subtopic || item.topic || 'Unknown', prerequisites: item.prerequisites || [], learning_materials: item.learning_materials || [], status: mastery?.status || 'Moderate', progress: mastery?.progress || '0%' };
    }) || [], [currentSubjectData, topicMasteryData]);

  const statsData = useMemo(() => {
    if (!currentSubjectData) return [];
    return [
      { label: 'Overall Performance', value: currentSubjectData.overall_average_score_display, icon: <TrendingUp sx={{ fontSize: 28 }} />, tooltip: currentSubjectData.overall_average_score_display },
      { label: 'Tests Completed', value: currentSubjectData['Tests Completed']?.toString() || '0', icon: <AssignmentTurnedIn sx={{ fontSize: 28 }} />, tooltip: 'Total tests completed' },
      { label: 'Topics Covered', value: currentSubjectData['Topic Mastery']?.length.toString() || '0', icon: <School sx={{ fontSize: 28 }} />, tooltip: 'Number of topics covered' },
      { label: 'Avg. Performance', value: typeof currentSubjectData.overall_average_performance === 'number' ? currentSubjectData.overall_average_performance.toFixed(2) : 'N/A', icon: <EmojiEvents sx={{ fontSize: 28 }} />, tooltip: String(currentSubjectData.overall_average_performance ?? 'N/A') },
    ];
  }, [currentSubjectData]);

  const strongTopicsList = useMemo(() => {
    const raw = selectedSubject === 'All'
      ? performanceData?.data?.['Strong Topics']
      : performanceData?.data?.Subjects?.[selectedSubject]?.['Strong Topics'];
    return parseTopicsToList(raw || '');
  }, [selectedSubject, performanceData]);

  const focusAreasList = useMemo(() => {
    if (selectedSubject === 'All') return parseTopicsToList(performanceData?.data?.['Focus Areas'] || '');
    const areas = performanceData?.data?.Subjects?.[selectedSubject]?.['AI-Generated Learning Recommendations']?.focus_areas;
    return parseTopicsToList((areas || []).join(', '));
  }, [selectedSubject, performanceData]);

  const aiRecommendations: AIRecommendations | undefined = currentSubjectData?.['AI-Generated Learning Recommendations'];
  const performanceTrendData = currentSubjectData?.['Performance Trend'] || [];

  const hasAiRec = !!(aiRecommendations && (
    aiRecommendations.recommended_learning_path ||
    aiRecommendations.improvement_tips?.length ||
    aiRecommendations.learning_materials?.length
  ));

  // ─── Filter Controls ────────────────────────────────────────────────────────

  const FilterRow = ({ topicVal, setTopicVal, subtopicVal, setSubtopicVal, subtopicList }: any) => (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <TextField select label="Topic" size="small" value={topicVal} onChange={e => setTopicVal(e.target.value)} sx={{ minWidth: 160 }}>
        <MenuItem value="">All</MenuItem>
        {topics.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
      </TextField>
      <TextField select label="Subtopic" size="small" value={subtopicVal} onChange={e => setSubtopicVal(e.target.value)} sx={{ minWidth: 160 }} disabled={!topicVal}>
        <MenuItem value="">All</MenuItem>
        {subtopicList.map((st: string) => <MenuItem key={st} value={st}>{st}</MenuItem>)}
      </TextField>
    </Box>
  );

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { sm: 2, xs: '16px 10px' }, bgcolor: '#f8fafc', minHeight: '100vh', margin: { sm: '-14px -16px -9px -9px', xs: '-8px' } }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 0, mb: 2, bgcolor: 'transparent' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', rowGap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: '#1e293b', fontSize: 22 }}>AI Progress Dashboard</Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: 13 }}>Comprehensive academic progress tracking and analytics</Typography>
          </Box>
          <Stack direction="row" spacing={2} flexWrap="wrap" gap={1.5} sx={{ width: { md: 'auto', xs: '100%' } }}>
            <Box sx={{ width: { md: 220, xs: '100%' } }}>
              <SearchableDropdown label="Select Student" options={studentOptions} value={selectedStudent} onChange={() => { }} labelKey="label" valueKey="value" size="small" disabled />
            </Box>
            <FormControl size="small" sx={{ minWidth: { md: 150, xs: '100%' }, bgcolor: 'white', ml: { md: 'auto', xs: '0px !important' } }}>
              <InputLabel sx={{ fontSize: 13 }}>Subject Filter</InputLabel>
              <Select value={selectedSubject} label="Subject Filter" onChange={e => setSelectedSubject(e.target.value)} startAdornment={<FilterList sx={{ mr: { md: 1, xs: 0 }, fontSize: 18 }} />} sx={{ fontWeight: 500, fontSize: 13 }}>
                {availableSubjects.map(s => <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      </Paper>

      {/* Loading */}
      {loadingPerformance && (
        <Paper sx={{ mb: 3, textAlign: 'center', py: 4, borderRadius: 2, border: '1px solid #e2e8f0' }}>
          <CircularProgress size={36} />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2, fontWeight: 600, fontSize: 13 }}>Loading performance data...</Typography>
        </Paper>
      )}

      {/* Stats */}
      {statsData.length > 0 && (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {statsData.map((item, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <StatCard {...item} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Strong Topics & Focus Areas */}
      {(strongTopicsList.length > 0 || focusAreasList.length > 0) && (
        <Grid container spacing={2.5} sx={{ mb: 0 }}>
          {strongTopicsList.length > 0 && (
            <TopicList title="Strong Topics" items={strongTopicsList} color="#10b981" bg="#f0fdf4" border="#86efac" icon={<CheckCircle sx={{ fontSize: 20 }} />} />
          )}
          {focusAreasList.length > 0 && (
            <TopicList title="Focus Areas" items={focusAreasList} color="#ef4444" bg="#fef2f2" border="#fca5a5" icon={<Assessment sx={{ fontSize: 20 }} />} />
          )}
        </Grid>
      )}

      {/* AI Recommendations */}
      {hasAiRec && (
        <Box sx={{ mb: { sm: 3, xs: 2 } }}>
          <Paper elevation={0} sx={{ borderRadius: 0, overflow: 'hidden', bgcolor: 'transparent' }}>
            <CardContent sx={{ pb: '0px !important', px: 0, pt: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '8px', bgcolor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main' }}>
                  <TipsAndUpdates sx={{ fontSize: 22 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>AI-Powered Learning Recommendations</Typography>
              </Box>
              {aiRecommendations?.improvement_tips?.length! > 0 && (
                <Paper sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 2.5, mb: 2.5, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                  {aiRecommendations?.recommended_learning_path && (
                    <Alert severity="info" icon={<AssignmentTurnedIn sx={{ fontSize: 20 }} />} sx={{ mb: 2.5, bgcolor: '#eff6ff', borderRadius: 2, border: '1px solid #bfdbfe', '& .MuiAlert-icon': { color: 'primary.main' } }}>
                      <AlertTitle sx={{ fontWeight: 700, color: '#1e40af', fontSize: 14 }}>Recommended Learning Path</AlertTitle>
                      <Typography component="div" sx={{ fontSize: 13, color: '#475569', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{aiRecommendations.recommended_learning_path}</Typography>
                    </Alert>
                  )}
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mb: 1.5, fontSize: 14 }}>Improvement Tips</Typography>
                  <List dense sx={{ py: 0 }}>
                    {aiRecommendations?.improvement_tips?.map((tip, i) => (
                      <ListItem key={i} sx={{ py: 1, px: 0, alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}><CheckCircle sx={{ fontSize: 18, color: '#10b981' }} /></ListItemIcon>
                        <ListItemText primary={tip} primaryTypographyProps={{ fontSize: 13, color: '#334155', lineHeight: 1.6 }} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </CardContent>
          </Paper>
        </Box>
      )}

      {/* Topic Mastery Table */}
      {topicMasteryData.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ p: { sm: 3, xs: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: 15, mb: 0 }}>Topic Mastery - {selectedSubject}</Typography>
                <FilterRow topicVal={tmTopicFilter} setTopicVal={setTmTopicFilter} subtopicVal={tmSubtopicFilter} setSubtopicVal={setTmSubtopicFilter} subtopicList={subtopics} />
              </Box>
              <CommonTable columns={topicColumns} data={filteredTopicMasteryData} enableTopToolbar={false} enableBottomToolbar={false} state={{ sorting }} onSortingChange={setSorting} />
            </CardContent>
          </Paper>
        </Box>
      )}

      {/* Test History */}
      {testHistoryData.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ p: { sm: 2.5, xs: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: 15, mb: 0 }}>Test History - {selectedSubject}</Typography>
                <FilterRow topicVal={thTopicFilter} setTopicVal={setThTopicFilter} subtopicVal={thSubtopicFilter} setSubtopicVal={setThSubtopicFilter} subtopicList={thSubtopics} />
              </Box>
              <CommonTable columns={testHistoryColumns} data={filteredTestHistoryData} enableTopToolbar={false} enableBottomToolbar={false} state={{ sorting }} onSortingChange={setSorting} />
              {aiRecommendations?.learning_materials?.length! > 0 && (
                <>
                  <Divider sx={{ bgcolor: '#ccc', mt: 2.5 }} />
                  <Box sx={{ mt: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mb: 2, fontSize: 14 }}>Recommended Resources</Typography>
                    <Stack spacing={1.5}>
                      {aiRecommendations?.learning_materials?.map((mat, i) => (
                        <ResourceLink key={i} href={mat.url || '#'} title={mat.title || 'Learning Resource'} />
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
            </CardContent>
          </Paper>
        </Box>
      )}

      {/* Performance Trend */}
      {performanceTrendData.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 700, color: '#1e293b', fontSize: 15 }}>Performance Trend - {selectedSubject}</Typography>
              <LineChart
                xAxis={[{ scaleType: 'band', data: performanceTrendData.map((d: any) => d.month), tickLabelStyle: { angle: 0, textAnchor: 'middle', fontSize: 11, fontWeight: 500 } }]}
                series={[{
                  data: performanceTrendData.map((d: any) => {
                    const match = (d.score || d.percentage || '0').toString().match(/[\d.]+/);
                    return match ? parseFloat(match[0]) : 0;
                  }),
                  label: 'Score', color: '#3b82f6', curve: 'natural', showMark: true, area: true,
                }]}
                height={320}
                margin={{ top: 20, right: 30, bottom: 50, left: 50 }}
                sx={{ '& .MuiLineElement-root': { strokeWidth: 2.5 } }}
              />
            </CardContent>
          </Paper>
        </Box>
      )}

      <LearningMaterialsDialog open={dialogOpen} onClose={() => setDialogOpen(false)} subtopic={selectedSubtopic} />
      {showCreateTest && <CreateTest userType="parent" onClose={() => setShowCreateTest(false)} onSubmit={() => setShowCreateTest(false)} />}
    </Box>
  );
};

export default ParentDashboard;

